<template>
  <main class="pw">
    <div class="container">
      <!-- Hero -->
      <section class="card card--hero">
        <div class="hero">
          <div class="hero__text">
            <h1>Change Password</h1>
            <p class="sub">Keep your account secure with a fresh password.</p>
          </div>
        </div>
      </section>

      <!-- Status banner -->
      <transition name="fade">
        <p v-if="errorMessage" class="status error" role="alert">{{ errorMessage }}</p>
      </transition>
      <transition name="fade">
        <p v-if="successMessage" class="status success" role="status">{{ successMessage }}</p>
      </transition>

      <!-- Form -->
      <section class="card">
        <h2 class="card__title">Update your password</h2>

        <form @submit.prevent="resetPassword" class="form">
          <!-- Current -->
          <div class="field">
            <label for="current">Current password</label>
            <div class="input-wrap">
              <input
                :type="showCurrent ? 'text' : 'password'"
                id="current"
                v-model.trim="currentPassword"
                required
                autocomplete="current-password"
                @keyup.caps-lock="caps = $event.getModifierState && $event.getModifierState('CapsLock')"
              />
              <button type="button" class="eye" @click="showCurrent = !showCurrent" :aria-pressed="showCurrent">
                {{ showCurrent ? 'Hide' : 'Show' }}
              </button>
            </div>
            <div v-if="caps" class="hint warn">Caps Lock is on.</div>
          </div>

          <!-- New -->
          <div class="field">
            <label for="new">New password</label>
            <div class="input-wrap">
              <input
                :class="['input', strengthClass]"
                :type="showNew ? 'text' : 'password'"
                id="new"
                v-model.trim="newPassword"
                required
                minlength="6"
                autocomplete="new-password"
                @input="errorMessage=''; successMessage=''"
              />
              <button type="button" class="eye" @click="showNew = !showNew" :aria-pressed="showNew">
                {{ showNew ? 'Hide' : 'Show' }}
              </button>
            </div>

            <!-- Strength meter -->
            <div class="meter" aria-hidden="true">
              <span :style="{ width: strengthPct + '%' }" :class="strengthClass"></span>
            </div>
            <div class="hint">Strength: <strong class="cap">{{ strengthLabel }}</strong></div>

            <!-- Checklist -->
            <ul class="checklist">
              <li :class="{ ok: newPassword.length >= 6 }">At least 6 characters</li>
              <li :class="{ ok: /[A-Z]/.test(newPassword) }">Uppercase letter</li>
              <li :class="{ ok: /[a-z]/.test(newPassword) }">Lowercase letter</li>
              <li :class="{ ok: /[0-9]/.test(newPassword) }">Number</li>
            </ul>
          </div>

          <!-- Confirm -->
          <div class="field">
            <label for="confirm">Confirm new password</label>
            <div class="input-wrap">
              <input
                :type="showConfirm ? 'text' : 'password'"
                id="confirm"
                v-model.trim="confirmNewPassword"
                required
                autocomplete="new-password"
              />
              <button type="button" class="eye" @click="showConfirm = !showConfirm" :aria-pressed="showConfirm">
                {{ showConfirm ? 'Hide' : 'Show' }}
              </button>
            </div>
            <div class="hint" :class="{ warn: confirmNewPassword && confirmNewPassword !== newPassword }">
              {{ confirmNewPassword && confirmNewPassword !== newPassword ? 'Does not match.' : 'Re‑enter to confirm.' }}
            </div>
          </div>

          <div class="actions">
            <button type="submit" class="btn btn--primary" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Change password</span>
              <span v-else>Updating…</span>
            </button>
          </div>
        </form>
      </section>
    </div>
  </main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PasswordReset',
  data() {
    return {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      errorMessage: '',
      successMessage: '',
      isSubmitting: false,
      showCurrent: false,
      showNew: false,
      showConfirm: false,
      caps: false
    };
  },
  computed: {
    strengthScore() {
      const p = this.newPassword || '';
      let s = 0;
      if (p.length >= 6) s++;
      if (/[A-Z]/.test(p)) s++;
      if (/[a-z]/.test(p)) s++;
      if (/[0-9]/.test(p)) s++;
      if (/[^A-Za-z0-9]/.test(p)) s++; // bonus for symbol
      return Math.min(s, 5);
    },
    strengthPct() {
      return (this.strengthScore / 5) * 100;
    },
    strengthLabel() {
      return ['Very weak','Weak','Fair','Good','Strong'][Math.max(0,this.strengthScore-1)] || 'Very weak';
    },
    strengthClass() {
      return this.strengthScore >= 4 ? 'good' : this.strengthScore >= 3 ? 'fair' : 'weak';
    }
  },
  methods: {
    resetPassword() {
      this.errorMessage = '';
      this.successMessage = '';

      if (this.newPassword !== this.confirmNewPassword) {
        this.errorMessage = 'New passwords do not match.';
        return;
      }
      if (this.newPassword.length < 6) {
        this.errorMessage = 'New password must be at least 6 characters.';
        return;
      }

      this.isSubmitting = true;
      axios.post(
        'etherscan/other/change_password.php',
        { currentPassword: this.currentPassword, newPassword: this.newPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(({data}) => {
        if (data.status === 'success') {
          this.successMessage = 'Password changed successfully.';
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmNewPassword = '';
        } else {
          this.errorMessage = data.message || 'Unable to change password.';
        }
      }).catch(() => {
        this.errorMessage = 'An error occurred. Please try again.';
      }).finally(() => {
        this.isSubmitting = false;
      });
    }
  },
  mounted() {
    document.body.classList.add('route-pw'); // prevent double bg, keep nav above
  },
  beforeUnmount() {
    document.body.classList.remove('route-pw');
  }
};
</script>

<style scoped>
/* Theme tokens */
.pw {
  --bg: #f7f9fc;
  --card: #ffffff;
  --ink: #1f2a37;
  --muted: #6b7280;
  --border: #e5e8ec;
  --accent: #35495e;
  --accent-2: #2c80b4;

  color: var(--ink);
  padding: 1.25rem 1rem 2.5rem;
  background: var(--bg);
}
.container { max-width: 720px; margin: 0 auto; }

/* Cards */
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1.2rem;
  margin-top: 1rem;
}
.card__title { margin: 0 0 .45rem; font-size: 1.05rem; }

/* Hero with subtle gradients */
.card--hero {
  margin-top: .25rem;
  background:
    radial-gradient(180px 120px at 85% 20%, rgba(44,128,180,.08), transparent 60%),
    radial-gradient(220px 160px at 10% 90%, rgba(53,73,94,.06), transparent 60%),
    linear-gradient(180deg, #ffffff, #f4f7fc);
}
.hero { display: grid; grid-template-columns: 1fr; gap: .4rem; }
.hero__text h1 { margin: 0; font-size: clamp(1.4rem, 1.2rem + 1vw, 1.9rem); }
.sub { margin: .2rem 0 0; color: var(--muted); }

/* Status banners */
.status {
  margin: .75rem 0;
  padding: .7rem .9rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  font-weight: 600;
}
.status.success { background: #f2fbf6; color: #1f6b3a; border-color: #cfe9d8; }
.status.error   { background: #fdeaea; color: #8f1d1d; border-color: #f4c7c7; }
.fade-enter-active,.fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from,.fade-leave-to { opacity: 0; }

/* Form */
.form { display: grid; gap: .8rem; }
.field { display: grid; gap: .35rem; }
label { color: var(--muted); font-size: .92rem; }
.input-wrap { position: relative; }
input {
  width: 100%;
  padding: .75rem 3.2rem .75rem .9rem; /* room for eye toggle */
  border-radius: 10px;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--ink);
  box-sizing: border-box;
}
input:focus { outline: none; box-shadow: 0 0 0 3px rgba(44,128,180,.18); border-color: #c5d9ea; }

/* Show/Hide toggle */
.eye {
  position: absolute;
  right: .45rem;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid var(--border);
  background: #fff;
  color: var(--accent);
  border-radius: 8px;
  padding: .25rem .5rem;
  font-weight: 700;
  cursor: pointer;
}
.eye:hover { filter: brightness(.98); }

/* Strength meter */
.meter {
  height: 6px;
  border-radius: 999px;
  background: #eef2f6;
  overflow: hidden;
  margin-top: .3rem;
}
.meter span {
  display: block;
  height: 100%;
  transition: width .25s ease;
}
.meter span.weak { background: #f9c5c1; }
.meter span.fair { background: #ffe2a9; }
.meter span.good { background: #cde9d6; }

.hint { color: var(--muted); font-size: .9rem; }
.hint.warn { color: #7a3b00; }

/* Checklist */
.checklist { list-style: none; padding: 0; margin: .2rem 0 0; display: grid; gap: .2rem; }
.checklist li { color: var(--muted); font-size: .9rem; }
.checklist li.ok { color: #1f6b3a; }

/* Buttons */
.btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: .75rem 1rem; border-radius: 10px; font-weight: 700; cursor: pointer;
  border: 1px solid var(--border); background: #fff; color: var(--accent);
  transition: filter 140ms ease, box-shadow 140ms ease;
}
.btn:hover { filter: brightness(.98); }
.btn--primary {
  border-color: transparent;
  background: linear-gradient(180deg, var(--accent-2), #1f6d9a);
  color: #fff; box-shadow: 0 6px 14px rgba(31,109,154,.18);
}

.actions { display: grid; justify-content: center; }

/* Route bg fix */
</style>
<style>
.route-pw #app > nav { z-index: 1000; }
.route-pw,
.route-pw #app,
.route-pw #app > main { background: #f7f9fc; }
</style>
